import { createContext, useContext } from 'react';

import type { ContainerQuery } from '../../common/containers';
import type {
  ControlAppearance,
  ControlCondense,
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariantInput,
  ControlWidth,
} from './types';
import { containers } from '../../common/containers';
import { normalizeControlVariant } from './normalizeControlVariant';

export type ControlContextValue = {
  appearance?: ControlAppearance;
  condense?: boolean | ControlCondense;
  ghost?: boolean;
  size?: ControlSize;
  variant?: ControlVariantInput;
  width?: ControlWidth;
  minWidth?: ControlMinWidth;
  maxWidth?: ControlMaxWidth;
};

const ControlContext = createContext<ControlContextValue | undefined>(undefined);

/**
 * Provides default appearance, size, and/or variant for descendent controls (e.g. Button, Select).
 * Pass only the keys you need; children will fall back to their own defaults for omitted keys.
 *
 * @example
 *   <ControlProvider value={{ size: 'small', variant: 'neutral' }}>
 *     <Button>Small secondary button</Button>
 *     <Select placeholder="Choose…" />
 *   </ControlProvider>
 */
export const ControlProvider = ControlContext.Provider;

/**
 * Returns the current control appearance from context if defined, otherwise returns the default value.
 */
export const useControlAppearance = (
  controlledValue: ControlAppearance | undefined,
  defaultValue: ControlAppearance = 'vivid',
): ControlAppearance => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.appearance ?? defaultValue;
};

const toContainerQueries = (value: ControlCondense): readonly ContainerQuery[] =>
  typeof value === 'string' ? [value] : value;

/** Default trigger for `condense={true}`: the application container at or below the desktop breakpoint. */
export const DEFAULT_CONTROL_CONDENSE: readonly ContainerQuery[] = [containers.application.notWide];

/**
 * Resolves a control's condense trigger: explicit queries, then a `ControlProvider`'s (e.g.
 * `ControlGroup`), then the default for `true`. `false` on the control always opts out.
 */
export const useControlCondense = (
  controlledValue?: boolean | ControlCondense,
): false | readonly ContainerQuery[] => {
  const contextValue = useContext(ControlContext)?.condense;
  if (controlledValue === false) return false;
  if (controlledValue !== undefined && controlledValue !== true) {
    return toContainerQueries(controlledValue);
  }
  if (contextValue !== undefined && contextValue !== false) {
    return contextValue === true ? DEFAULT_CONTROL_CONDENSE : toContainerQueries(contextValue);
  }
  return controlledValue === true ? DEFAULT_CONTROL_CONDENSE : false;
};

/**
 * Returns the current control ghost flag from context if defined, otherwise returns the default value.
 */
export const useControlGhost = (
  controlledValue: boolean | undefined,
  defaultValue = false,
): boolean => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.ghost ?? defaultValue;
};

/**
 * Returns the current control size from context if defined, otherwise returns the default value.
 */
export const useControlSize = (
  controlledValue?: ControlSize,
  defaultValue: ControlSize = 'medium',
): ControlSize => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.size ?? defaultValue;
};

/**
 * Returns the current control variant from context if defined, otherwise returns the default value.
 */
export const useControlVariant = (
  controlledValue?: ControlVariantInput,
  defaultValue: ControlVariantInput = 'brand',
) => {
  const contextValue = useContext(ControlContext);
  return normalizeControlVariant(controlledValue ?? contextValue?.variant ?? defaultValue);
};

/**
 * Returns the current control width from context if defined, otherwise returns undefined.
 */
export const useControlWidth = (controlledValue?: ControlWidth): ControlWidth | undefined => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.width;
};

/**
 * Returns the current control min width from context if defined, otherwise returns undefined.
 */
export const useControlMinWidth = (
  controlledValue?: ControlMinWidth,
): ControlMinWidth | undefined => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.minWidth;
};

/**
 * Returns the current control max width from context if defined, otherwise returns undefined.
 */
export const useControlMaxWidth = (
  controlledValue?: ControlMaxWidth,
): ControlMaxWidth | undefined => {
  const contextValue = useContext(ControlContext);
  return controlledValue ?? contextValue?.maxWidth;
};
